import { useCallback, useState } from "react";
import { Link } from "react-router";
import { useMutation } from "@apollo/client";
import {
  ArrowLeft,
  Monitor,
  Tablet,
  Smartphone,
  Settings,
  Home,
  Undo2,
  Redo2,
  RotateCcw,
  Eye,
  Copy,
  Check,
  ExternalLink,
  Clock,
  Save,
} from "lucide-react";
import type { PageForEdit } from "../pages/types";
import type { EditorState, Viewport } from "./useEditorState";
import { useActiveTenant } from "../../../auth/AuthProvider";
import { publicSiteBase } from "../../../hooks/publicSiteUrl";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { Tooltip } from "../../../components/ui/Tooltip";
import { useToast } from "../../../components/ui/Toast";
import {
  SplitButton,
  SplitMenuItem,
  SplitMenuDivider,
} from "../../../components/ui/SplitButton";
import { MINT_PREVIEW_TOKEN } from "../pages/mutations";

interface EditorTopBarProps {
  page: PageForEdit;
  editor: EditorState;
  showSettings: boolean;
  onToggleSettings: () => void;
  showHistory?: boolean;
  onToggleHistory?: () => void;
  /** When previewing a version, the version number being viewed */
  previewingVersion?: number | null;
  /** Label describing which version is being previewed */
  previewingLabel?: string;
  /** Callback to exit version preview and return to editing the draft */
  onExitHistory?: () => void;
}

const VP_OPTIONS: { key: Viewport; icon: typeof Monitor; label: string }[] = [
  { key: "desktop", icon: Monitor, label: "Desktop" },
  { key: "tablet", icon: Tablet, label: "Tablet" },
  { key: "mobile", icon: Smartphone, label: "Mobile" },
];

export function EditorTopBar({
  page,
  editor,
  showSettings,
  onToggleSettings,
  showHistory,
  onToggleHistory,
  previewingVersion,
  previewingLabel,
  onExitHistory,
}: EditorTopBarProps) {
  const [showPublishPop, setShowPublishPop] = useState(false);
  const [showRevertPop, setShowRevertPop] = useState(false);
  const [showPreviewPop, setShowPreviewPop] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const tenant = useActiveTenant();
  const toast = useToast();

  const [mintToken, { loading: minting }] = useMutation(MINT_PREVIEW_TOKEN);

  // Revert is available when a published version exists and the draft
  // differs from it (either saved server-side or locally unsaved edits).
  const canRevert =
    page.publishedContent !== null &&
    (page.draftContent !== page.publishedContent || editor.isDirty);

  // Preview is available when the page has draft content
  const hasDraft = page.draftContent !== null;

  const handlePreview = useCallback(async () => {
    try {
      const result = await mintToken({ variables: { id: page.id } });
      const token = result.data?.mintPreviewToken?.previewToken;
      if (!token) throw new Error("No token returned");

      // Build the preview URL. Base is environment-aware so dev links point
      // at the local frontend (the tenant's real domain won't know a locally
      // minted token).
      const base = publicSiteBase(tenant.domain);
      const pagePath = page.isHome ? "/" : `/${page.slug}`;
      const url = `${base}${pagePath}?preview=${token}`;
      setPreviewUrl(url);
      setShowPreviewPop(true);
      setCopied(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      toast.push({
        kind: "error",
        title: "Couldn't generate preview link",
        description: message,
      });
    }
  }, [mintToken, page.id, page.isHome, page.slug, tenant.domain, toast]);

  const handleCopy = useCallback(async () => {
    if (!previewUrl) return;
    try {
      await navigator.clipboard.writeText(previewUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the input text
      const input = document.querySelector<HTMLInputElement>(
        "[data-preview-url]",
      );
      if (input) {
        input.select();
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  }, [previewUrl]);

  // Derive a single status label that covers every combination of
  // page.status, draft-vs-published content, and local unsaved edits.
  // Priority: unsaved local edits > draft-only page > saved draft
  // changes on a published page > published in sync > archived.
  const { statusLabel, statusClass, statusDot } = (() => {
    if (editor.isDirty) {
      return {
        statusLabel: "Unsaved changes",
        statusClass: "bg-spark/10 text-spark border border-spark/20",
        statusDot: true,
      };
    }
    if (page.status === "draft") {
      return {
        statusLabel: "Draft",
        statusClass: "bg-amber-50 text-amber-700 border border-amber-200",
        statusDot: false,
      };
    }
    if (page.status === "archived") {
      return {
        statusLabel: "Archived",
        statusClass: "bg-canvas-2 text-ink-4 border border-ink-6",
        statusDot: false,
      };
    }
    // published
    if (page.draftContent !== page.publishedContent) {
      return {
        statusLabel: "Draft changes",
        statusClass: "bg-[#f0e6cf] text-[#7a6230]",
        statusDot: false,
      };
    }
    return {
      statusLabel: "Published",
      statusClass: "bg-sage-s text-sage",
      statusDot: false,
    };
  })();

  /* Shared markup blocks that appear in both wide and narrow layouts */

  const undoRedoButtons = (
    <div className="flex items-center gap-0.5">
      <button
        title="Undo (Cmd+Z)"
        disabled={!editor.canUndo}
        onClick={() => editor.undo()}
        className="flex items-center px-2 py-[5px] rounded-lg text-ink-4 hover:bg-paper-2 hover:text-ink-2 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-4 transition-colors"
      >
        <Undo2 size={15} strokeWidth={1.8} />
      </button>
      <button
        title="Redo (Cmd+Shift+Z)"
        disabled={!editor.canRedo}
        onClick={() => editor.redo()}
        className="flex items-center px-2 py-[5px] rounded-lg text-ink-4 hover:bg-paper-2 hover:text-ink-2 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-4 transition-colors"
      >
        <Redo2 size={15} strokeWidth={1.8} />
      </button>
    </div>
  );

  const viewportToggle = (
    <div className="flex bg-paper-2 rounded-[10px] p-[3px]">
      {VP_OPTIONS.map(({ key, icon: Icon, label }) => (
        <button
          key={key}
          title={label}
          onClick={() => editor.setViewport(key)}
          className={`flex items-center px-2.5 py-[5px] rounded-lg transition-colors ${
            editor.viewport === key
              ? "bg-paper-hi text-ink-2 shadow-card"
              : "text-ink-5 hover:text-ink-3"
          }`}
        >
          <Icon size={15} strokeWidth={1.6} />
        </button>
      ))}
    </div>
  );

  const historySettingsButtons = (
    <>
      {onToggleHistory && (
        <Tooltip label="Version history" side="bottom">
          <button
            onClick={onToggleHistory}
            className={`flex items-center px-2 py-[5px] rounded-lg transition-colors ${
              showHistory
                ? "border border-tide bg-tide-ss text-tide"
                : "text-ink-4 hover:bg-paper-2 hover:text-ink-2"
            }`}
          >
            <Clock size={15} strokeWidth={1.8} />
          </button>
        </Tooltip>
      )}
      <Tooltip label="Page settings" side="bottom">
        <button
          onClick={onToggleSettings}
          className={`flex items-center px-2 py-[5px] rounded-lg transition-colors ${
            showSettings
              ? "border border-tide bg-tide-ss text-tide"
              : "text-ink-4 hover:bg-paper-2 hover:text-ink-2"
          }`}
        >
          <Settings size={15} strokeWidth={1.8} />
        </button>
      </Tooltip>
    </>
  );

  const divider = <div className="w-px h-5 bg-ink-6" />;

  const splitButton = (
    <SplitButton
      label={editor.isPublishing ? "Publishing..." : "Publish"}
      onClick={() => setShowPublishPop((prev) => !prev)}
      disabled={editor.isPublishing}
    >
      <SplitMenuItem
        icon={<Save size={15} strokeWidth={1.8} />}
        onClick={async () => {
          await editor.saveDraft();
        }}
        disabled={!editor.isDirty || editor.isSaving}
        kbd="&#8984;S"
      >
        {editor.isSaving ? "Saving..." : "Save draft"}
      </SplitMenuItem>
      {hasDraft && (
        <SplitMenuItem
          icon={<Eye size={15} strokeWidth={1.8} />}
          onClick={handlePreview}
          disabled={minting}
        >
          {minting ? "Generating..." : "Preview draft..."}
        </SplitMenuItem>
      )}
      {canRevert && (
        <>
          <SplitMenuDivider />
          <SplitMenuItem
            icon={<RotateCcw size={12} strokeWidth={2} />}
            variant="danger"
            onClick={() => setShowRevertPop(true)}
            disabled={editor.isReverting}
          >
            Discard draft...
          </SplitMenuItem>
        </>
      )}
    </SplitButton>
  );

  return (
    <header className="bg-paper-hi hair-b relative z-40">
      {/* Row 1: identity + primary action */}
      <div className="h-[54px] flex items-center gap-3 px-3.5">
        {/* Back link -- navigation is intercepted by useBlocker when dirty */}
        <Link
          to="/website/pages"
          className="flex items-center gap-1.5 text-ink-4 text-[13px] font-medium rounded-lg px-2 py-1.5 hover:bg-paper-2 hover:text-ink-2 transition-colors"
        >
          <ArrowLeft size={13} strokeWidth={2} />
          <span className="hidden lg:inline">Pages</span>
        </Link>

        {/* Separator -- hidden on narrow */}
        <span className="text-ink-6 hidden lg:inline">/</span>

        {/* Page switcher (read-only for now) -- narrower max-w on small screens */}
        <Tooltip label={page.title || page.slug} side="bottom">
          <button className="flex items-center gap-2 px-2.5 py-1.5 rounded-[10px] text-[14px] font-semibold text-ink border border-transparent hover:border-ink-6 hover:bg-paper transition-colors">
            <span className="truncate max-w-[140px] lg:max-w-[200px]">
              {page.title || page.slug}
            </span>
            <span className="text-ink-5 text-[10px] shrink-0">&#9662;</span>
          </button>
        </Tooltip>

        {/* Home chip -- hidden on narrow */}
        {page.isHome && (
          <span className="hidden lg:inline-flex items-center gap-[5px] text-[11px] font-semibold text-ink-4 bg-paper-2 border border-ink-6 px-2 py-0.5 rounded-full font-mono">
            <Home size={11} strokeWidth={2} />
            Home page &mdash; always at /
          </span>
        )}

        {/* Status pill -- always a plain span */}
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-[3px] rounded-full ${statusClass}`}
        >
          {statusDot && (
            <span className="w-[7px] h-[7px] rounded-full bg-spark pulse-soft" />
          )}
          {statusLabel}
        </span>

        {/* History mode elements */}
        {showHistory && (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-[3px] rounded-full bg-tide-s text-tide-d">
            Version history
          </span>
        )}
        {previewingVersion != null && previewingLabel && (
          <span className="text-[12px] text-ink-4">
            Viewing: {previewingLabel}
          </span>
        )}
        {showHistory && onExitHistory && (
          <button
            onClick={onExitHistory}
            className="text-[11px] font-semibold text-ink-4 hover:text-ink-2 px-2 py-1 rounded-lg hover:bg-paper-2 transition-colors"
          >
            Exit history
          </button>
        )}

        {/* Tools inline -- ONLY on wide (>= lg) */}
        <div className="hidden lg:flex items-center gap-3 ml-auto">
          {undoRedoButtons}
          {divider}
          {viewportToggle}
          {divider}
          {historySettingsButtons}
          {divider}
        </div>

        {/* On narrow, push the split button to the right */}
        <div className="lg:hidden ml-auto" />

        {/* Split publish button -- both layouts */}
        {splitButton}
      </div>

      {/* Row 2: tools -- ONLY on narrow (< lg) */}
      <div className="flex lg:hidden items-center gap-3 px-3.5 h-[54px] border-t border-ink-6 overflow-x-auto">
        {undoRedoButtons}
        {divider}
        {viewportToggle}
        {divider}
        {historySettingsButtons}
      </div>

      {/* Publish confirm popover */}
      {showPublishPop && (
        <div className="absolute top-[48px] right-3.5 z-[70] w-[280px] bg-paper-hi border border-ink-6 rounded-[14px] p-4 shadow-lift">
          <h4 className="text-[14px] font-semibold mb-1.5">
            Publish &ldquo;{page.title || page.slug}&rdquo;?
          </h4>
          <p className="text-[12.5px] text-ink-4 leading-relaxed mb-3.5">
            Your draft goes live at{" "}
            <b className="font-mono text-[11.5px]">
              {page.isHome
                ? `${tenant.domain}/`
                : `${tenant.domain}/${page.slug}`}
            </b>{" "}
            immediately. The current live version is kept, so you can revert.
          </p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowPublishPop(false)}
              className="text-[13px] font-semibold rounded-[10px] px-3.5 py-2 border border-ink-6 bg-paper-hi text-ink-2 hover:bg-paper-2 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                setShowPublishPop(false);
                await editor.publish();
              }}
              className="text-[13px] font-semibold rounded-[10px] px-3.5 py-2 border border-tide bg-tide text-white hover:bg-tide-d transition-colors"
            >
              Publish
            </button>
          </div>
        </div>
      )}

      {/* Preview link popover */}
      {showPreviewPop && previewUrl && (
        <div className="absolute top-[48px] right-3.5 z-[70] w-[380px] bg-paper-hi border border-ink-6 rounded-[14px] p-4 shadow-lift">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[14px] font-semibold">Preview link</h4>
            <button
              onClick={() => setShowPreviewPop(false)}
              className="text-ink-4 hover:text-ink-2 text-[18px] leading-none px-1"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
          <p className="text-[12px] text-ink-4 mb-3 leading-relaxed">
            Share this link to preview the draft. Expires in 24 hours.
          </p>
          <div className="flex gap-1.5">
            <input
              data-preview-url
              type="text"
              readOnly
              value={previewUrl}
              className="flex-1 min-w-0 rounded-md border bg-canvas px-2.5 py-2 font-mono text-[11px] text-ink-3 hair focus:outline-none"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={handleCopy}
              title={copied ? "Copied!" : "Copy to clipboard"}
              className="flex items-center gap-1.5 rounded-md border bg-paper-hi px-3 py-2 text-[12px] font-medium text-ink-2 hover:bg-paper-2 hair transition-colors"
            >
              {copied ? (
                <Check size={13} strokeWidth={2} className="text-sage" />
              ) : (
                <Copy size={13} strokeWidth={1.8} />
              )}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2.5 flex items-center gap-1.5 text-[12px] font-medium text-tide hover:text-tide-d transition-colors"
          >
            <ExternalLink size={12} strokeWidth={1.8} />
            Open in new tab
          </a>
        </div>
      )}

      <ConfirmDialog
        open={showRevertPop}
        variant="danger"
        title="Discard draft changes?"
        description={
          <>
            This reverts the page to the last published version. All draft
            edits{editor.isDirty ? " (including unsaved changes)" : ""} will
            be lost.
          </>
        }
        confirmLabel={editor.isReverting ? "Reverting..." : "Discard draft"}
        onConfirm={async () => {
          setShowRevertPop(false);
          await editor.revertToPublished();
        }}
        onCancel={() => setShowRevertPop(false)}
        confirming={editor.isReverting}
      />
    </header>
  );
}
